import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import {
    ColumnDataType,
    ConfigurationColumn,
    ConfigurationRows,
    NumberConfiguration
} from '../../model/resource';
import { NotificationService } from '../../services/notificationService';

@Component({
    selector: 'formulario-dinamico',
    templateUrl: './formulario-dinamico.component.html',
    styleUrls: ['./formulario-dinamico.component.css']
})
export class FormularioDinamicoComponent implements OnInit, OnDestroy {
    formulario!: FormGroup;

    @Input() entidade: any = {};
    @Input() linhas!: ConfigurationRows[];
    @Input() colunas!: ConfigurationColumn[];

    columnEnum = ColumnDataType;

    private destroy$ = new Subject<void>();

    @Output() entidadeChange: EventEmitter<any> = new EventEmitter<any>();

    constructor(private notify: NotificationService) { }

    ngOnInit(): void {
        this.configureLinhas();
        this.setup();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['resource']) this.setup();
    }

    configureLinhas() {
        if (!this.linhas || this.linhas.length == 0) {
            this.linhas = [];
            this.linhas[0] = { colunas: this.colunas };
        }
    }

    setup = () => {
        this.configureFormGroup();
    };

    private configureFormGroup() {
        const grupo: any = {};

        this.colunas
            .filter((coluna) => coluna.type != ColumnDataType.Midia)
            .forEach((coluna) => {
                const validators: any = this.createValidators(coluna);
                const control = new FormControl('', validators);
                this.createValueChangeEmmiter(control, coluna);
                grupo[coluna.field] = control;

                if (coluna.defaultValue) {
                    control.setValue(coluna.defaultValue, { emitEvent: false });
                }
            });


        this.formulario = new FormGroup(grupo);

        this.applyDefaultValues();
    }

    private applyDefaultValues() {
        this.colunas.forEach(coluna => {
            if (
                this.entidade[coluna.field] === undefined &&
                coluna.defaultValue !== undefined
            ) {
                this.entidade[coluna.field] = coluna.defaultValue;
                this.entidadeChange.emit(this.entidade);
            }
        });
    }

    private createValueChangeEmmiter(control: FormControl, coluna: ConfigurationColumn) {
        if (coluna.type == ColumnDataType.Text || !coluna.type) {
            control.valueChanges.pipe(debounceTime(2000)).pipe(takeUntil(this.destroy$)).subscribe((value) => {
                this.formularioChanged(coluna);
            });
        } else if (coluna.type != ColumnDataType.Date && coluna.type != ColumnDataType.Checkbox) {
            control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
                this.formularioChanged(coluna);
            });
        }
    }

    private formularioChanged(coluna: ConfigurationColumn) {
        this.entidadeChange.emit(this.entidade);
        this.callShowCampoFormulario();

        this.callOnChange(coluna);
    }

    private callOnChange(coluna: ConfigurationColumn) {
        if (coluna.onChange) {
            coluna.onChange(this.entidade);
        }
    }


    changeDate(coluna: ConfigurationColumn) {
        // evento de change da data foi criado separado para evitar dissincronismo na informação
        // quando evento era tratado da msm forma que os outros inputs, a informação setada no entidade era sempre a do estado anterior
        this.formularioChanged(coluna);
    }

    changeCheckbox(coluna: ConfigurationColumn) {
        this.formularioChanged(coluna);
    }

    private createValidators(coluna: ConfigurationColumn) {
        const validators: any = [];

        if (coluna.required) validators.push(Validators.required);

        coluna.validation?.forEach((validation) => {
            if (validation.type == 'email') {
                validators.push(Validators.email);
            }
        });

        if (coluna.type == ColumnDataType.Number && coluna.numberConfiguration)
            this.inserirValidatorsNumber(coluna.numberConfiguration, validators);

        return validators;
    }

    isValid() {
        this.formulario.markAllAsTouched();
        return this.formulario.valid;
    }

    resetarFormulario() {
        this.formulario.reset();
    }

    getDadosFormularioSalvar() {
        const formData: any = {};

        Object.entries(this.entidade).forEach(([key, value]) => {
            let valueSave = value;

            const columnResource: ConfigurationColumn | undefined = this.colunas.find(
                (coluna) => coluna.field == key
            );
            if (columnResource && columnResource.getValueBeforSave) {
                valueSave = columnResource.getValueBeforSave(value);
            }

            formData[key] = valueSave;
        });

        return formData;
    }

    tratarErroRequisicao(erro: any) {
        if (erro.error.erro) {
            this.notify.error(erro.error.erro);
        } else if (erro.error) {
            const erroApi = erro.error;

            Object.keys(erroApi).forEach((key) => {
                erroApi[key].forEach((mensagemErro: string) => {
                    this.setErroFormControl(key, mensagemErro);
                });
            });
        }
    }

    private setErroFormControl(field: string, mensagemErro: string) {
        const formControl = this.formulario.get(field);
        formControl?.setErrors({ apiError: mensagemErro });
    }

    toggleVisibilityPass(inputPassword: HTMLInputElement) {
        if (inputPassword.type == 'text') inputPassword.type = 'password';
        else inputPassword.type = 'text';
    }

    private inserirValidatorsNumber(config: NumberConfiguration, validators: Validators[]) {
        if (this.isNotNullOrUndefined(config.maxValue)) {
            validators.push(Validators.max(config.maxValue!));
        }
        if (this.isNotNullOrUndefined(config.minValue)) {
            validators.push(Validators.min(config.minValue!));
        }
    }

    private callShowCampoFormulario() {
        this.colunas.forEach(coluna => {
            if (coluna.showCampoFormulario) {
                const exibir = coluna.showCampoFormulario(this.entidade);
                coluna.visible = exibir;
                if (!exibir) {
                    this.formulario.get(coluna.field)?.setValue(null, { emitEvent: false });
                }
            }
        });
    }

    private isNotNullOrUndefined(value: any) {
        return !(value == null || value == undefined);
    }
}
